import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface LoaderConfig {
  id?: string;
  timeout?: number;
}

@Injectable({
  providedIn: 'root',
})
export class LoaderService {
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private activeLoaders = new Map<string, boolean>();
  private timeouts = new Map<string, ReturnType<typeof setTimeout>>();

  public loading$: Observable<boolean> = this.loadingSubject.asObservable();

  show(config?: LoaderConfig): void {
    const id = config?.id || 'global';

    this.activeLoaders.set(id, true);
    this.updateLoadingState();

    if (config?.timeout) {
      this.setAutoTimeout(id, config.timeout);
    }
  }

  hide(id?: string): void {
    const loaderId = id || 'global';

    this.clearTimeout(loaderId);
    this.activeLoaders.delete(loaderId);
    this.updateLoadingState();
  }

  isLoading(id?: string): boolean {
    if (id) {
      return this.activeLoaders.has(id);
    }
    return this.loadingSubject.value;
  }

  hideAll(): void {
    this.activeLoaders.clear();
    this.clearAllTimeouts();
    this.loadingSubject.next(false);
  }

  private updateLoadingState(): void {
    this.loadingSubject.next(this.activeLoaders.size > 0);
  }

  private setAutoTimeout(id: string, timeout: number): void {
    this.clearTimeout(id);
    const timeoutId = setTimeout(() => {
      this.hide(id);
    }, timeout);
    this.timeouts.set(id, timeoutId);
  }

  private clearTimeout(id: string): void {
    const timeoutId = this.timeouts.get(id);
    if (timeoutId) {
      clearTimeout(timeoutId);
      this.timeouts.delete(id);
    }
  }

  private clearAllTimeouts(): void {
    this.timeouts.forEach((timeout) => clearTimeout(timeout));
    this.timeouts.clear();
  }
}
